import React, { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { requestPending, requestPristine, requestSuccess } from "state/reducer/requestStatusSlice";
import { getReservations } from "net/b2b";
import { useTranslation } from "react-i18next";
import NoDataFound from "components/NoDataFound";
import useRequestStatus from "hooks/useRequestStatus";
import { DATE_FORMAT, RequestStatus, CalendarSlotStatus, RoutePath, TIME_FORMAT, EntityType, SupplyingResultIdStatus, AppUserRole } from "shared/constants";
import SimpleTable, { SimpleTableType } from "components/SimpleTable";
import { v4 as uuid } from "uuid";
import moment from "moment";
import TableRighArrow from "components/TableRightArrow";
import { useNavigate } from "react-router-dom";
import DsBadge from "components/DsBadge";
import "views/contractView/styles/contractDataStyles.css";
import { getBookingBadgeStatus } from "./shared/bookingBadgeStatus";
import { getBookingLegend, getTableBookingRows, parseService } from "views/contractView/shared/listUtils";
import { IParseDateFormatType, parseDateForDS } from "shared/utils/dateUtils";
import { Table } from "@ds/ds-snam/react";
import { ITableCell, ITableConfig, ITableRow } from "@ds/ds-snam/dist/models/tables";
import { getDSTableShaderElementsForFilterColumn, getDSTableSharedElements, getTableCellsHeaderSimple } from "shared/utils/dsComponentUtils";
import { stat } from "fs";
import { AppUser } from "ooAuth2/appUser";
import useAppUser from "hooks/useAppUser";

export interface IBookingElement{
    codeIup: string,
    reservationCode:string,
    date: number, //timestamp del giorno dello slot
    time: string, //stringa nel formato "HH:mm-HH:mm" inizio-fine slot
    businessName: string // nome del cliente
    businessId:string, //identificativo del cliente
    status: CalendarSlotStatus, //0-7
    prereservation: boolean, //quando stato in pending, determina se possibile inserire la prenotazione (true)
    prereservationSales:boolean,
    jarvisAvailable: number, //da usare solo nel dettaglio di inserimento
    jarvisShipper: number //da usare solo nel dettaglio di inserimento
    shipperName:string
    service:number
    contractId:string,
    supplyingResultId:string,
    isDelegated: boolean, //TRUE se è di un mio delegato, e non ci potrò accedere
    finalBusiness: string | null,
}
const BookingList: React.FC<{}> = (): JSX.Element => {
    const [bookings, setBookings] = useState<IBookingElement[]>([]);
    //const [bookingsFiltered, setBookingsFiltered] = useState<IBookingElement[]>([]);
    const appUser: AppUser | undefined = useAppUser();
    const appUserRole: AppUserRole | undefined = appUser?.getRole();
    
    const [confirmedBookings, setConfirmedBookings] = useState<IBookingElement[]>([]);
    const [closedBookings, setClosedBookings] = useState<IBookingElement[]>([]);
    const [vacantBookings, setVacantBookings] = useState<IBookingElement[]>([]);
    const [cancelledBookings, setCancelledBookings] = useState<IBookingElement[]>([]);
    const [otherBookings, setOtherBookings] = useState<IBookingElement[]>([]);

    const [filtersConfirmed, setFiltersConfirmed] = useState<{ [key: string]: string }>({
        col1: "",
        col4: "",
        col5: "",
        col6: "",
    });
    const [filtersClosed, setFiltersClosed] = useState<{ [key: string]: string }>({
        col1: "",
        col4: "",
        col5: "",
        col6: "",
    });
    const [filtersVacant, setFiltersVacant] = useState<{ [key: string]: string }>({
        col1: "",
        col4: "",
        col5: "",
        col6: "",
    });
    const [filtersCancelled, setFiltersCancelled] = useState<{ [key: string]: string }>({
        col1: "",
        col4: "",
        col5: "",
        col6: "",
    });
    const [filtersOthers, setFiltersOthers] = useState<{ [key: string]: string }>({
        col1: "",
        col4: "",
        col5: "",
        col6: "",
    });
    const [isAscendingConfirmed, setIsAscendingConfirmed] = useState<boolean | null>(null); // Stato per tracciare l'ordine
    const [isAscendingClosed, setIsAscendingClosed] = useState<boolean | null>(null); // Stato per tracciare l'ordine
    const [isAscendingVacant, setIsAscendingVacant] = useState<boolean | null>(null); // Stato per tracciare l'ordine
    const [isAscendingCancelled, setIsAscendingCancelled] = useState<boolean | null>(null); // Stato per tracciare l'ordine
    const [isAscendingOthers, setIsAscendingOthers] = useState<boolean | null>(null); // Stato per tracciare l'ordine

    const dispatch = useDispatch();
    const { t } = useTranslation(['b2b', 'app']);
    const requestStatus = useRequestStatus();
    const navigate = useNavigate();

    const loadData = useCallback(() => {
        dispatch(requestPending());
        getReservations()
            .then((response) => {
                dispatch(requestSuccess());
                //setBookingsFiltered(response.data as IBookingElement[])
                setBookings(response.data as IBookingElement[]);
                const confirmed = (response.data as IBookingElement[]).filter(b => b.status === CalendarSlotStatus.CONFIRMED);
                const closed = (response.data as IBookingElement[]).filter(b => b.status === CalendarSlotStatus.CLOSED);
                const vacant = (response.data as IBookingElement[]).filter(b => b.status === CalendarSlotStatus.VACANT);
                const cancelled = (response.data as IBookingElement[]).filter(b => b.status === CalendarSlotStatus.CANCELLED);
                const others = (response.data as IBookingElement[]).filter(b => (b.status !== CalendarSlotStatus.CONFIRMED && b.status !== CalendarSlotStatus.CLOSED && b.status !== CalendarSlotStatus.VACANT && b.status !== CalendarSlotStatus.CANCELLED));
                setConfirmedBookings(confirmed);
                setClosedBookings(closed);
                setVacantBookings(vacant);
                setCancelledBookings(cancelled);
                setOtherBookings(others);
            })
            .catch(error => {
                dispatch(requestPristine());
                console.log(error);
            })
    }, [dispatch]);

    useEffect(() => {
        loadData();
        return () => {
            dispatch(requestPristine());
        }
    }, [loadData, dispatch]);



    // Gestione dei cambiamenti dei filtri
    const handleFilterChangeConfirmed = (columnKey: string, value: string) => {
        setFiltersConfirmed((prevFilters) => ({
            ...prevFilters,
            [columnKey]: value,
        }));
    };
    // Gestione dei cambiamenti dei filtri
    const handleFilterChangeClosed = (columnKey: string, value: string) => {
        setFiltersClosed((prevFilters) => ({
            ...prevFilters,
            [columnKey]: value,
        }));
    };
    // Gestione dei cambiamenti dei filtri
    const handleFilterChangeVacant = (columnKey: string, value: string) => {
        setFiltersVacant((prevFilters) => ({
            ...prevFilters,
            [columnKey]: value,
        }));
    };
    // Gestione dei cambiamenti dei filtri
    const handleFilterChangeOthers = (columnKey: string, value: string) => {
        setFiltersOthers((prevFilters) => ({
            ...prevFilters,
            [columnKey]: value,
        }));
    };
    // Gestione dei cambiamenti dei filtri
    const handleFilterChangeCancelled = (columnKey: string, value: string) => {
        setFiltersCancelled((prevFilters) => ({
            ...prevFilters,
            [columnKey]: value,
        }));
    };

    // Gestione del click sulla colonna 1 (ordinamento)
    const handleSortColumn1Confirmed = () => {
        setConfirmedBookings((prevList) => {
            let newOrder;
            if (isAscendingConfirmed === null) {
                // Se non abbiamo ancora uno stato iniziale, ordina in ordine decrescente
                newOrder = [...prevList].sort((a, b) => {
                    const dateA = moment(parseDateForDS(moment.unix(a.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    const dateB = moment(parseDateForDS(moment.unix(b.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    return dateB.getTime() - dateA.getTime(); // Ordine decrescente
                });
                setIsAscendingConfirmed(false); // Imposta l'ordine come decrescente
            } else {
                // Alterna tra crescente e decrescente
                newOrder = [...prevList].sort((a, b) => {
                    const dateA = moment(parseDateForDS(moment.unix(a.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    const dateB = moment(parseDateForDS(moment.unix(b.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    return isAscendingConfirmed
                        ? dateB.getTime() - dateA.getTime() // Ordine decrescente
                        : dateA.getTime() - dateB.getTime(); // Ordine crescente
                });
                setIsAscendingConfirmed(!isAscendingConfirmed); // Inverti l'ordine
            }

            return newOrder;
        });
    };
    const handleSortColumn1Closed = () => {
        setClosedBookings((prevList) => {
            let newOrder;
            if (isAscendingClosed === null) {
                // Se non abbiamo ancora uno stato iniziale, ordina in ordine decrescente
                newOrder = [...prevList].sort((a, b) => {
                    const dateA = moment(parseDateForDS(moment.unix(a.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    const dateB = moment(parseDateForDS(moment.unix(b.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    return dateB.getTime() - dateA.getTime(); // Ordine decrescente
                });
                setIsAscendingClosed(false); // Imposta l'ordine come decrescente
            } else {
                // Alterna tra crescente e decrescente
                newOrder = [...prevList].sort((a, b) => {
                    const dateA = moment(parseDateForDS(moment.unix(a.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    const dateB = moment(parseDateForDS(moment.unix(b.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    return isAscendingClosed
                        ? dateB.getTime() - dateA.getTime() // Ordine decrescente
                        : dateA.getTime() - dateB.getTime(); // Ordine crescente
                });
                setIsAscendingClosed(!isAscendingClosed); // Inverti l'ordine
            }

            return newOrder;
        });
    };

    const handleSortColumn1Others = () => {
        setOtherBookings((prevList) => {
            let newOrder;
            if (isAscendingOthers === null) {
                // Se non abbiamo ancora uno stato iniziale, ordina in ordine decrescente
                newOrder = [...prevList].sort((a, b) => {
                    const dateA = moment(parseDateForDS(moment.unix(a.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    const dateB = moment(parseDateForDS(moment.unix(b.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    return dateB.getTime() - dateA.getTime(); // Ordine decrescente
                });
                setIsAscendingOthers(false); // Imposta l'ordine come decrescente
            } else {
                // Alterna tra crescente e decrescente
                newOrder = [...prevList].sort((a, b) => {
                    const dateA = moment(parseDateForDS(moment.unix(a.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    const dateB = moment(parseDateForDS(moment.unix(b.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    return isAscendingOthers
                        ? dateB.getTime() - dateA.getTime() // Ordine decrescente
                        : dateA.getTime() - dateB.getTime(); // Ordine crescente
                });
                setIsAscendingOthers(!isAscendingOthers); // Inverti l'ordine
            }

            return newOrder;
        });
    };

    const handleSortColumn1Cancelled = () => {
        setCancelledBookings((prevList) => {
            let newOrder;
            if (isAscendingCancelled === null) {
                // Se non abbiamo ancora uno stato iniziale, ordina in ordine decrescente
                newOrder = [...prevList].sort((a, b) => {
                    const dateA = moment(parseDateForDS(moment.unix(a.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    const dateB = moment(parseDateForDS(moment.unix(b.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    return dateB.getTime() - dateA.getTime(); // Ordine decrescente
                });
                setIsAscendingCancelled(false); // Imposta l'ordine come decrescente
            } else {
                // Alterna tra crescente e decrescente
                newOrder = [...prevList].sort((a, b) => {
                    const dateA = moment(parseDateForDS(moment.unix(a.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    const dateB = moment(parseDateForDS(moment.unix(b.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1), "DD.MM.YYYY").toDate();
                    return isAscendingCancelled
                        ? dateB.getTime() - dateA.getTime() // Ordine decrescente
                        : dateA.getTime() - dateB.getTime(); // Ordine crescente
                });
                setIsAscendingCancelled(!isAscendingCancelled); // Inverti l'ordine
            }

            return newOrder;
        });
    };

    useEffect(() => {
        let myFilteredList: IBookingElement[] = [];
        for (let i in bookings) {
            const serviceStr = parseService(bookings[i].service); // Ottieni la stringa del servizio
            if (
                bookings[i].reservationCode.toLocaleLowerCase().includes(filtersConfirmed.col1.toLocaleLowerCase()) &&
                bookings[i].businessName.toLocaleLowerCase().includes(filtersConfirmed.col4.toLocaleLowerCase()) &&
                bookings[i].shipperName.toLocaleLowerCase().includes(filtersConfirmed.col5.toLocaleLowerCase()) &&
                serviceStr.toLocaleLowerCase().includes(filtersConfirmed.col6.toLocaleLowerCase()) &&
                bookings[i].status === CalendarSlotStatus.CONFIRMED // Filtro per lo stato "CONFERMATO"
            ) {
                myFilteredList.push(bookings[i]);
            }
        }
        setConfirmedBookings(myFilteredList);
    }, [filtersConfirmed, bookings]);

    useEffect(() => {
        let myFilteredList: IBookingElement[] = [];
        for (let i in bookings) {
            const serviceStr = parseService(bookings[i].service); // Ottieni la stringa del servizio
            if (
                bookings[i].reservationCode.toLocaleLowerCase().includes(filtersClosed.col1.toLocaleLowerCase()) &&
                bookings[i].businessName.toLocaleLowerCase().includes(filtersClosed.col4.toLocaleLowerCase()) &&
                bookings[i].shipperName.toLocaleLowerCase().includes(filtersClosed.col5.toLocaleLowerCase()) &&
                serviceStr.toLocaleLowerCase().includes(filtersClosed.col6.toLocaleLowerCase()) &&
                bookings[i].status === CalendarSlotStatus.CLOSED // Filtro per lo stato "CONFERMATO"
            ) {
                myFilteredList.push(bookings[i]);
            }
        }
        setClosedBookings(myFilteredList);
    }, [filtersClosed, bookings]);

    useEffect(() => {
        let myFilteredList: IBookingElement[] = [];
        for (let i in bookings) {
            const serviceStr = parseService(bookings[i].service); // Ottieni la stringa del servizio
            if (
                bookings[i].reservationCode.toLocaleLowerCase().includes(filtersVacant.col1.toLocaleLowerCase()) &&
                bookings[i].businessName.toLocaleLowerCase().includes(filtersVacant.col4.toLocaleLowerCase()) &&
                bookings[i].shipperName.toLocaleLowerCase().includes(filtersVacant.col5.toLocaleLowerCase()) &&
                serviceStr.toLocaleLowerCase().includes(filtersVacant.col6.toLocaleLowerCase()) &&
                bookings[i].status === CalendarSlotStatus.VACANT // Filtro per lo stato "VACANT"
            ) {
                myFilteredList.push(bookings[i]);
            }
        }
        setVacantBookings(myFilteredList);
    }, [filtersVacant, bookings]);

    useEffect(() => {
        let myFilteredList: IBookingElement[] = [];
        for (let i in bookings) {
            const serviceStr = parseService(bookings[i].service); // Ottieni la stringa del servizio
            if (
                bookings[i].reservationCode.toLocaleLowerCase().includes(filtersOthers.col1.toLocaleLowerCase()) &&
                bookings[i].businessName.toLocaleLowerCase().includes(filtersOthers.col4.toLocaleLowerCase()) &&
                bookings[i].shipperName.toLocaleLowerCase().includes(filtersOthers.col5.toLocaleLowerCase()) &&
                serviceStr.toLocaleLowerCase().includes(filtersOthers.col6.toLocaleLowerCase()) &&
                (bookings[i].status !== CalendarSlotStatus.CONFIRMED && bookings[i].status !== CalendarSlotStatus.CLOSED && bookings[i].status !== CalendarSlotStatus.VACANT && bookings[i].status !== CalendarSlotStatus.CANCELLED) // Filtro per Others
            ) {
                myFilteredList.push(bookings[i]);
            }
        }
        setOtherBookings(myFilteredList);
    }, [filtersOthers, bookings]);
    
    useEffect(() => {
        let myFilteredList: IBookingElement[] = [];
        for (let i in bookings) {
            const serviceStr = parseService(bookings[i].service); // Ottieni la stringa del servizio
            if (
                bookings[i].reservationCode.toLocaleLowerCase().includes(filtersCancelled.col1.toLocaleLowerCase()) &&
                bookings[i].businessName.toLocaleLowerCase().includes(filtersCancelled.col4.toLocaleLowerCase()) &&
                bookings[i].shipperName.toLocaleLowerCase().includes(filtersCancelled.col5.toLocaleLowerCase()) &&
                serviceStr.toLocaleLowerCase().includes(filtersCancelled.col6.toLocaleLowerCase()) &&
                bookings[i].status === CalendarSlotStatus.CANCELLED // Filtro per lo stato "CANCELLED"
            ) {
                myFilteredList.push(bookings[i]);
            }
        }
        setCancelledBookings(myFilteredList);
    }, [filtersCancelled, bookings]);
    

    const getTableRows = (list: IBookingElement[]): ITableRow[] => {
        return list.map((booking) => {
            const tableCells: ITableCell[] = [
                { 
                    type: "text-badge", 
                    text: booking.reservationCode,
                    badgeStatus: getBookingBadgeStatus(booking.status) as "confirm" | "error" | "warning" | "custom",
                    badgeCustomColor: booking.status === CalendarSlotStatus.CLOSED 
                        ? "#320216" 
                        : booking.status === CalendarSlotStatus.CANCELLED 
                            ? "#9A0557" 
                            : booking.status === CalendarSlotStatus.VACANT ?
                            "#267f8f"
                            :"#4956B0"
                },
                { 
                    type: "text", 
                    text: parseDateForDS(moment.unix(booking.date).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1),
                },
                { type: "text", text: booking.time },
                { type: "text", text: booking.businessName },
                { type: "text", text: booking.shipperName },
                { type: "text", text: parseService(booking.service) },
            ];
            if (booking.status === CalendarSlotStatus.CLOSED) {
                let statusText = "";
                switch (booking.supplyingResultId.toString()) {
                    case SupplyingResultIdStatus.CANCELED.toString():
                        statusText= t("booking.bookingList.others.data1")
                        break;
                    case SupplyingResultIdStatus.FTD.toString():
                        statusText= "Failure To Deliver"
                        break;
                    case SupplyingResultIdStatus.FTT.toString():
                        statusText= "Failure To Take"
                        break;
                    case SupplyingResultIdStatus.NEGATIVE.toString():
                        statusText= "Negative"
                        break;
                    case SupplyingResultIdStatus.POSITIVE.toString():
                        statusText= t("booking.bookingList.others.data3")
                        break;
                    case SupplyingResultIdStatus.UNKNOWN.toString():
                        statusText= ""/*t("booking.bookingList.others.data2")*/
                        break;
                    default:
                        statusText= ""
                        break;
                }
                tableCells.push({ type: "text", text: statusText });
                tableCells.push({ type: "text", text: booking.finalBusiness || "" });
            } else {
                tableCells.push({ type: "text", text: booking.finalBusiness || "" });
            }
            
            // Questo è il return della funzione passata alla map
            return {
                tableCells,
                ...(booking.isDelegated !== true && {
                    rightIcon: "icon-chevron-right",
                }),
            };
        });
    
        // Questo è il return della funzione getTableRows
    };

    
    
    /*const myTableConfig: ITableConfig = {
        ...getDSTableSharedElements(560),
        tableHeader: {
            tableCellsHeader: [
                          {
                              ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.0") as string,"col1","20%","Annulla","Conferma",t('general.buttons.search', { ns: 'app' })),
                              searchBoxValue:filtersConfirmed["col1"], //<---
                          },
                          getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.1") as string,"col2","16%","left",true),
                          getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.2") as string,"col3","16%","left"),
                          {
                              ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.3") as string,"col4","16%","Annulla","Conferma",t('general.buttons.search', { ns: 'app' })),
                              searchBoxValue:filtersConfirmed["col4"], //<---
                          },
                          {
                            ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.4") as string,"col5","16%","Annulla","Conferma",t('general.buttons.search', { ns: 'app' })),
                            searchBoxValue:filtersConfirmed["col5"], //<---
                          },
                          {
                            ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.5") as string,"col6","16%","Annulla","Conferma",t('general.buttons.search', { ns: 'app' })),
                            searchBoxValue:filtersConfirmed["col6"], //<---
                          }
            ],
            hasSearchBox: true,
        },
        tableRows: getTableRows(),
    };*/
    const myTableConfigConfirmed: ITableConfig = {
        ...getDSTableSharedElements(confirmedBookings.length?290:0),
        tableHeader: {
            tableCellsHeader: [
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.0") as string,"col1","16%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersConfirmed["col1"], //<---
                },
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.1") as string,"col2","14%","left",true),
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.2") as string,"col3","14%","left"),
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.3") as string,"col4","14%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersConfirmed["col4"], //<---
                },
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.4") as string,"col5","14%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersConfirmed["col5"], //<---
                },
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.5") as string,"col6","14%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersConfirmed["col6"], //<---
                },
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.8") as string,"col7","14%","left")
            ],
            hasSearchBox: true,
        },
        tableRows: getTableRows(confirmedBookings),
    };
    const myTableConfigClosed: ITableConfig = {
        ...getDSTableSharedElements(closedBookings.length?290:0),
        tableHeader: {
            tableCellsHeader: [
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.0") as string,"col1","15%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersClosed["col1"], //<---
                },
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.1") as string,"col2","12%","left",true),
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.2") as string,"col3","12%","left"),
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.3") as string,"col4","12%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersClosed["col4"], //<---
                },
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.4") as string,"col5","12%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersClosed["col5"], //<---
                },
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.5") as string,"col6","12%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersClosed["col6"], //<---
                },
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.7") as string,"col3","12%","left"),
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.8") as string,"col7","13%","left"),
            ],
            hasSearchBox: true,
        },
        tableRows: getTableRows(closedBookings),
    };
    const myTableConfigVacant: ITableConfig = {
        ...getDSTableSharedElements(vacantBookings.length?290:0),
        tableHeader: {
            tableCellsHeader: [
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.0") as string,"col1","16%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersVacant["col1"], //<---
                },
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.1") as string,"col2","14%","left",true),
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.2") as string,"col3","14%","left"),
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.3") as string,"col4","14%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersVacant["col4"], //<---
                },
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.4") as string,"col5","14%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersVacant["col5"], //<---
                },
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.5") as string,"col6","14%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersVacant["col6"], //<---
                },
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.8") as string,"col7","14%","left")
            ],
            hasSearchBox: true,
        },
        tableRows: getTableRows(vacantBookings),
    };
    const myTableConfigCancelled: ITableConfig = {
        ...getDSTableSharedElements(cancelledBookings.length?290:0),
        tableHeader: {
            tableCellsHeader: [
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.0") as string,"col1","16%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersCancelled["col1"], //<---
                },
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.1") as string,"col2","14%","left",true),
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.2") as string,"col3","14%","left"),
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.3") as string,"col4","14%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersCancelled["col4"], //<---
                },
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.4") as string,"col5","14%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersCancelled["col5"], //<---
                },
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.5") as string,"col6","14%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersCancelled["col6"], //<---
                },
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.8") as string,"col7","14%","left"),
            ],
            hasSearchBox: true,
        },
        tableRows: getTableRows(cancelledBookings),
    };
    const myTableConfigOthers: ITableConfig = {
        ...getDSTableSharedElements(otherBookings.length?290:0),
        tableHeader: {
            tableCellsHeader: [
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.0") as string,"col1","16%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersOthers["col1"], //<---
                },
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.1") as string,"col2","14%","left",true),
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.2") as string,"col3","14%","left"),
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.3") as string,"col4","14%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersOthers["col4"], //<---
                },
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.4") as string,"col5","14%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersOthers["col5"], //<---
                },
                {
                    ...getDSTableShaderElementsForFilterColumn(t("booking.bookingList.tableColumns.5") as string,"col6","14%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                    searchBoxValue:filtersOthers["col6"], //<---
                },
                    getTableCellsHeaderSimple(t("booking.bookingList.tableColumns.8") as string,"col7","14%","left")
            ],
            hasSearchBox: true,
        },
        tableRows: getTableRows(otherBookings),
    };

    return (
        <>
            <p className="pageTitle" style={{ marginBottom: "10px" }}>{t('booking.bookingList.title')}</p>
            {/*<p className="pageSubTitle">{t("booking.bookingList.subTitle")}</p>*/}

            {(bookings && bookings.length!==0) /*&& bookingsFiltered.length*/ ?
                <>
                    <div style={{ marginBottom: "25px" }}>
                        {confirmedBookings/*.length!==0*/ &&
                            <><Table 
                                tableConfig={myTableConfigConfirmed} 
                                style={{}}
                                onInputSearchBarChange={(event:Event)=>{
                                    //console.log((event as CustomEvent).detail.searchedText + " - " + (event as CustomEvent).detail.cells)
                                }}
                                //FILTERS
                                onClickRightAction={(event:Event)=>{
                                    //console.log(event)
                                    const edit:boolean = ((confirmedBookings[(event as CustomEvent).detail.row].status===CalendarSlotStatus.PENDING||confirmedBookings[(event as CustomEvent).detail.row].status===CalendarSlotStatus.CONFIRMED) && confirmedBookings[(event as CustomEvent).detail.row].prereservation)
                                    navigate(RoutePath.B2B_BOOKINGS_INFO + `/${confirmedBookings[(event as CustomEvent).detail.row].codeIup}`,{state:{edit:edit}});
                                }}
                                onResetSearch={(event:Event)=>{
                                    //console.log('cliccata cella: ' + (event as CustomEvent).detail.cells);
                                    switch ((event as CustomEvent).detail.cells) {
                                        case 0:
                                            handleFilterChangeConfirmed("col1", "");
                                            break;
                                        case 3:
                                            handleFilterChangeConfirmed("col4", "");
                                            break;
                                        case 4:
                                            handleFilterChangeConfirmed("col5", "");
                                            break;
                                        case 5:
                                            handleFilterChangeConfirmed("col6", "");
                                            break;
                                        default:
                                            break;
                                    }
                                }}
                                onSubmitSearchedText={(event:Event)=>{
                                    const focusColtemp = (event as CustomEvent).detail.cells;
                                    let focusCol = '';
                                    switch (focusColtemp) {
                                        case 0:
                                            focusCol = "col1";
                                            break;
                                        case 1:
                                            focusCol = "col2";
                                            break;
                                        case 2:
                                            focusCol = "col3";
                                            break;
                                        case 3:
                                            focusCol = "col4";
                                            break;
                                        case 4:
                                            focusCol = "col5";
                                            break;
                                        case 5:
                                            focusCol = "col6";
                                            break;
                                        default:
                                            break;
                                    }
                                    const focusValue = (event as CustomEvent).detail.searchedText;
                                    handleFilterChangeConfirmed(focusCol, focusValue)
                                }}
                                onTableInputChange={(event:Event)=>{
                                    //console.log(event)
                                    const focusCol = (event as CustomEvent).detail.col;
                                    const focusValue = (event as CustomEvent).detail.value;
                                    //console.log(focusCol + " - " + focusValue)
                                    //handleFilterChangeConfirmed(focusCol, focusValue)
                                }}
                                onColumnSorting={(event:Event)=>{
                                    //console.log((event as CustomEvent).detail.columnID) 
                                    
                                }}
                            />
                            {getBookingLegend(t,[CalendarSlotStatus.CONFIRMED])}</>
                        }
                    </div>

                    <div style={{ marginBottom: "25px" }}>
                        {(otherBookings && appUserRole!== AppUserRole.ACCESS_STAFF/*.length!==0*/)  &&
                            <><Table 
                                tableConfig={myTableConfigOthers} 
                                style={{}}
                                onInputSearchBarChange={(event:Event)=>{
                                    //console.log((event as CustomEvent).detail.searchedText + " - " + (event as CustomEvent).detail.cells)
                                }}
                                //FILTERS
                                onClickRightAction={(event:Event)=>{
                                    //console.log(event)
                                    const edit:boolean = (otherBookings[(event as CustomEvent).detail.row].status===CalendarSlotStatus.PENDING && otherBookings[(event as CustomEvent).detail.row].prereservation)
                                    navigate(RoutePath.B2B_BOOKINGS_INFO + `/${otherBookings[(event as CustomEvent).detail.row].codeIup}`,{state:{edit:edit}});
                                }}
                                onResetSearch={(event:Event)=>{
                                    //console.log('cliccata cella: ' + (event as CustomEvent).detail.cells);
                                    switch ((event as CustomEvent).detail.cells) {
                                        case 0:
                                            handleFilterChangeOthers("col1", "");
                                            break;
                                        case 3:
                                            handleFilterChangeOthers("col4", "");
                                            break;
                                        case 4:
                                            handleFilterChangeOthers("col5", "");
                                            break;
                                        case 5:
                                            handleFilterChangeOthers("col6", "");
                                            break;
                                        default:
                                            break;
                                    }
                                }}
                                onSubmitSearchedText={(event:Event)=>{
                                    const focusColtemp = (event as CustomEvent).detail.cells;
                                    let focusCol = '';
                                    switch (focusColtemp) {
                                        case 0:
                                            focusCol = "col1";
                                            break;
                                        case 1:
                                            focusCol = "col2";
                                            break;
                                        case 2:
                                            focusCol = "col3";
                                            break;
                                        case 3:
                                            focusCol = "col4";
                                            break;
                                        case 4:
                                            focusCol = "col5";
                                            break;
                                        case 5:
                                            focusCol = "col6";
                                            break;
                                        default:
                                            break;
                                    }
                                    const focusValue = (event as CustomEvent).detail.searchedText;
                                    handleFilterChangeOthers(focusCol, focusValue)
                                }}
                                onTableInputChange={(event:Event)=>{
                                //console.log(event)
                                    const focusCol = (event as CustomEvent).detail.col;
                                    const focusValue = (event as CustomEvent).detail.value;
                                    //console.log(focusCol + " - " + focusValue)
                                    //handleFilterChangeOthers(focusCol, focusValue)
                                }}
                                onColumnSorting={(event:Event)=>{
                                    //console.log((event as CustomEvent).detail.columnID) 
                                    
                                }}
                            />
                            {getBookingLegend(t,[CalendarSlotStatus.PENDING, CalendarSlotStatus.REJECTED, CalendarSlotStatus.SLOT_DELETED])}
                            </>
                        }
                    </div>

                    <div style={{ marginBottom: "25px" }}>
                        {(cancelledBookings && appUserRole!== AppUserRole.ACCESS_STAFF) &&
                            <><Table 
                                tableConfig={myTableConfigCancelled} 
                                style={{}}
                                onInputSearchBarChange={(event:Event)=>{
                                    //console.log((event as CustomEvent).detail.searchedText + " - " + (event as CustomEvent).detail.cells)
                                }}
                                //FILTERS
                                onClickRightAction={(event:Event)=>{
                                    //console.log(event)
                                    const edit:boolean = (cancelledBookings[(event as CustomEvent).detail.row].status===CalendarSlotStatus.CANCELLED && cancelledBookings[(event as CustomEvent).detail.row].prereservation)
                                    navigate(RoutePath.B2B_BOOKINGS_INFO + `/${cancelledBookings[(event as CustomEvent).detail.row].codeIup}`,{state:{edit:edit}});
                                }}
                                onResetSearch={(event:Event)=>{
                                    //console.log('cliccata cella: ' + (event as CustomEvent).detail.cells);
                                    switch ((event as CustomEvent).detail.cells) {
                                        case 0:
                                            handleFilterChangeCancelled("col1", "");
                                            break;
                                        case 3:
                                            handleFilterChangeCancelled("col4", "");
                                            break;
                                        case 4:
                                            handleFilterChangeCancelled("col5", "");
                                            break;
                                        case 5:
                                            handleFilterChangeCancelled("col6", "");
                                            break;
                                        default:
                                            break;
                                    }
                                }}
                                onSubmitSearchedText={(event:Event)=>{
                                    const focusColtemp = (event as CustomEvent).detail.cells;
                                    let focusCol = '';
                                    switch (focusColtemp) {
                                        case 0:
                                            focusCol = "col1";
                                            break;
                                        case 1:
                                            focusCol = "col2";
                                            break;
                                        case 2:
                                            focusCol = "col3";
                                            break;
                                        case 3:
                                            focusCol = "col4";
                                            break;
                                        case 4:
                                            focusCol = "col5";
                                            break;
                                        case 5:
                                            focusCol = "col6";
                                            break;
                                        default:
                                            break;
                                    }
                                    const focusValue = (event as CustomEvent).detail.searchedText;
                                    handleFilterChangeCancelled(focusCol, focusValue)
                                }}
                                onTableInputChange={(event:Event)=>{
                                    //console.log(event)
                                    const focusCol = (event as CustomEvent).detail.col;
                                    const focusValue = (event as CustomEvent).detail.value;
                                    //console.log(focusCol + " - " + focusValue)
                                    //handleFilterChangeCancelled(focusCol, focusValue)
                                }}
                                onColumnSorting={(event:Event)=>{
                                    //console.log((event as CustomEvent).detail.columnID) 
                                    
                                }}
                            />
                            {getBookingLegend(t,[CalendarSlotStatus.CANCELLED])}</>
                        }
                    </div>

                    <div style={{ marginBottom: "25px" }}>
                        {(closedBookings && appUserRole!== AppUserRole.ACCESS_STAFF) &&
                            <><Table 
                                tableConfig={myTableConfigClosed} 
                                style={{}}
                                onInputSearchBarChange={(event:Event)=>{
                                    //console.log((event as CustomEvent).detail.searchedText + " - " + (event as CustomEvent).detail.cells)
                                }}
                                //FILTERS
                                onClickRightAction={(event:Event)=>{
                                    //console.log(event)
                                    const edit:boolean = (closedBookings[(event as CustomEvent).detail.row].status===CalendarSlotStatus.CLOSED && closedBookings[(event as CustomEvent).detail.row].prereservation)
                                    navigate(RoutePath.B2B_BOOKINGS_INFO + `/${closedBookings[(event as CustomEvent).detail.row].codeIup}`,{state:{edit:edit}});
                                }}
                                onResetSearch={(event:Event)=>{
                                    //console.log('cliccata cella: ' + (event as CustomEvent).detail.cells);
                                    switch ((event as CustomEvent).detail.cells) {
                                        case 0:
                                            handleFilterChangeClosed("col1", "");
                                            break;
                                        case 3:
                                            handleFilterChangeClosed("col4", "");
                                            break;
                                        case 4:
                                            handleFilterChangeClosed("col5", "");
                                            break;
                                        case 5:
                                            handleFilterChangeClosed("col6", "");
                                            break;
                                        default:
                                            break;
                                    }
                                }}
                                onSubmitSearchedText={(event:Event)=>{
                                    const focusColtemp = (event as CustomEvent).detail.cells;
                                    let focusCol = '';
                                    switch (focusColtemp) {
                                        case 0:
                                            focusCol = "col1";
                                            break;
                                        case 1:
                                            focusCol = "col2";
                                            break;
                                        case 2:
                                            focusCol = "col3";
                                            break;
                                        case 3:
                                            focusCol = "col4";
                                            break;
                                        case 4:
                                            focusCol = "col5";
                                            break;
                                        case 5:
                                            focusCol = "col6";
                                            break;
                                        default:
                                            break;
                                    }
                                    const focusValue = (event as CustomEvent).detail.searchedText;
                                    handleFilterChangeClosed(focusCol, focusValue)
                                }}
                                onTableInputChange={(event:Event)=>{
                                    //console.log(event)
                                    const focusCol = (event as CustomEvent).detail.col;
                                    const focusValue = (event as CustomEvent).detail.value;
                                    //console.log(focusCol + " - " + focusValue)
                                    //handleFilterChangeClosed(focusCol, focusValue)
                                }}
                                onColumnSorting={(event:Event)=>{
                                    //console.log((event as CustomEvent).detail.columnID) 
                                    
                                }}
                            />
                            {getBookingLegend(t,[CalendarSlotStatus.CLOSED])}</>
                        }
                    </div>

                    <div style={{ marginBottom: "25px" }}>
                        {(vacantBookings && appUserRole!== AppUserRole.ACCESS_STAFF) &&
                            <><Table 
                                tableConfig={myTableConfigVacant} 
                                style={{}}
                                onInputSearchBarChange={(event:Event)=>{
                                    //console.log((event as CustomEvent).detail.searchedText + " - " + (event as CustomEvent).detail.cells)
                                }}
                                //FILTERS
                                onClickRightAction={(event:Event)=>{
                                    //console.log(event)
                                    const edit:boolean = (vacantBookings[(event as CustomEvent).detail.row].status===CalendarSlotStatus.VACANT && vacantBookings[(event as CustomEvent).detail.row].prereservation)
                                    navigate(RoutePath.B2B_BOOKINGS_INFO + `/${vacantBookings[(event as CustomEvent).detail.row].codeIup}`,{state:{edit:edit}});
                                }}
                                onResetSearch={(event:Event)=>{
                                    //console.log('cliccata cella: ' + (event as CustomEvent).detail.cells);
                                    switch ((event as CustomEvent).detail.cells) {
                                        case 0:
                                            handleFilterChangeVacant("col1", "");
                                            break;
                                        case 3:
                                            handleFilterChangeVacant("col4", "");
                                            break;
                                        case 4:
                                            handleFilterChangeVacant("col5", "");
                                            break;
                                        case 5:
                                            handleFilterChangeVacant("col6", "");
                                            break;
                                        default:
                                            break;
                                    }
                                }}
                                 onSubmitSearchedText={(event:Event)=>{
                                    const focusColtemp = (event as CustomEvent).detail.cells;
                                    let focusCol = '';
                                    switch (focusColtemp) {
                                        case 0:
                                            focusCol = "col1";
                                            break;
                                        case 1:
                                            focusCol = "col2";
                                            break;
                                        case 2:
                                            focusCol = "col3";
                                            break;
                                        case 3:
                                            focusCol = "col4";
                                            break;
                                        case 4:
                                            focusCol = "col5";
                                            break;
                                        case 5:
                                            focusCol = "col6";
                                            break;
                                        default:
                                            break;
                                    }
                                    const focusValue = (event as CustomEvent).detail.searchedText;
                                    handleFilterChangeVacant(focusCol, focusValue)
                                }}
                                onTableInputChange={(event:Event)=>{
                                    //console.log(event)
                                    const focusCol = (event as CustomEvent).detail.col;
                                    const focusValue = (event as CustomEvent).detail.value;
                                    //console.log(focusCol + " - " + focusValue)
                                    //handleFilterChangeVacant(focusCol, focusValue)
                                }}
                                onColumnSorting={(event:Event)=>{
                                    //console.log((event as CustomEvent).detail.columnID) 
                                    
                                }}
                            />
                            {getBookingLegend(t,[CalendarSlotStatus.VACANT])}</>
                        }
                    </div>

                </>
            : (bookings.length===0 ? <NoDataFound
                    title={t('booking.bookingList.noData.title')}//"Non sono presenti prenotazioni"
                    subTitle={t('booking.bookingList.noData.subTitle')}
            /> : <></>)}
        </>
    );
}
export default BookingList;